const activeFlights = new Map();
const blockedMetadataRetries = new Set();
let authorizedScope;
const authorizedScopeStorageKey = "view-patient-sync-scope";

const normalizeOrgId = (orgId) =>
  orgId === undefined || orgId === null ? null : String(orgId);
const normalizeUserId = (userId) =>
  userId === undefined || userId === null ? null : String(userId);
const patientSyncScope = (orgId, userId) => {
  const normalizedOrgId = normalizeOrgId(orgId);
  const normalizedUserId = normalizeUserId(userId);
  return normalizedOrgId === null || normalizedUserId === null
    ? null
    : `${normalizedOrgId}:${normalizedUserId}`;
};

export const patientSyncFlightKey = ({ orgId, userId, authToken, mrn }) =>
  JSON.stringify([
    normalizeOrgId(orgId),
    normalizeUserId(userId),
    authToken || null,
    mrn === undefined || mrn === null ? null : String(mrn),
  ]);

export const patientSyncPatientKey = ({ orgId, userId, mrn }) =>
  JSON.stringify([
    normalizeOrgId(orgId),
    normalizeUserId(userId),
    mrn === undefined || mrn === null ? null : String(mrn),
  ]);

export const blockPatientSyncMetadataRetry = (key) =>
  blockedMetadataRetries.add(key);

export const clearPatientSyncMetadataRetry = (key) =>
  blockedMetadataRetries.delete(key);

export const isPatientSyncMetadataRetryBlocked = (key) =>
  blockedMetadataRetries.has(key);

export const isPatientSyncOrgReady = (orgId, userId) => {
  const scope = patientSyncScope(orgId, userId);
  if (scope === null) return false;

  if (authorizedScope === undefined) {
    authorizedScope = localStorage.getItem(authorizedScopeStorageKey);
  }
  return authorizedScope === scope;
};

export const markPatientSyncOrgLoaded = (orgId, userId) => {
  const scope = patientSyncScope(orgId, userId);
  if (scope !== null) {
    authorizedScope = scope;
    localStorage.setItem(authorizedScopeStorageKey, scope);
  }
};

export const createPatientSyncRetryScheduler = ({
  isOnline = () => navigator.onLine,
  setTimer = (operation, delay) => window.setTimeout(operation, delay),
  clearTimer = (timer) => window.clearTimeout(timer),
} = {}) => {
  const attempts = new Map();
  const timers = new Map();

  const schedule = (key, retry, isCurrent = () => true) => {
    if (timers.has(key) || !isOnline() || !isCurrent()) return;
    const attempt = attempts.get(key) || 0;
    const delay = Math.min(1000 * 2 ** attempt, 30000);
    attempts.set(key, attempt + 1);
    const timer = setTimer(async () => {
      timers.delete(key);
      if (!isOnline() || !isCurrent()) return;
      try {
        await retry();
      } catch (error) {
        schedule(key, retry, isCurrent);
      }
    }, delay);
    timers.set(key, timer);
  };

  const complete = (key) => {
    const timer = timers.get(key);
    if (timer !== undefined) clearTimer(timer);
    timers.delete(key);
    attempts.delete(key);
  };

  const dispose = () => {
    timers.forEach(clearTimer);
    timers.clear();
    attempts.clear();
  };

  return { complete, dispose, schedule };
};

export const runPatientSyncFlight = (key, operation) => {
  const existing = activeFlights.get(key);
  if (existing) return existing;

  const started = Promise.resolve().then(operation);
  const tracked = started.finally(() => {
    if (activeFlights.get(key) === tracked) activeFlights.delete(key);
  });
  activeFlights.set(key, tracked);
  return tracked;
};

export default runPatientSyncFlight;
